import { PatientInfoMapper } from "@/data/mapper/patient/patient-info-mapper";
import { safeMapToEntity } from "@/data/repo-Implement/safe-map-to-entity";
import { PatientSource } from "@/data/source/patient-source";
import {
  LOW_COMPLIANCE_THRESHOLD,
  PatientDetail,
  PatientListFilter,
  PatientSummary,
} from "@/domain/entity/patient/patient-info-entity";
import { PatientRepository } from "@/domain/repository/patient-repository";
import { SATError, SATResult } from "@sadtx/sat-error";

export class PatientRepositoryImpl implements PatientRepository {
  constructor(private readonly patientSource: PatientSource) {}

  async fetchPatients(filter?: PatientListFilter): Promise<SATResult<PatientSummary[], SATError>> {
    return this.fetchPatientsByAdmin(filter);
  }

  async fetchPatientsByDoctor(filter?: PatientListFilter): Promise<SATResult<PatientSummary[], SATError>> {
    const result = await this.patientSource.fetchPatientsByDoctor();
    return this.mapPatientList(result, filter, "PatientRepositoryImpl::fetchPatientsByDoctor");
  }

  async fetchPatientsByAdmin(filter?: PatientListFilter): Promise<SATResult<PatientSummary[], SATError>> {
    const result = await this.patientSource.fetchPatientsByAdmin();
    return this.mapPatientList(result, filter, "PatientRepositoryImpl::fetchPatientsByAdmin");
  }

  private mapPatientList(
    result: SATResult<unknown[], SATError>,
    filter: PatientListFilter | undefined,
    logContext: string,
  ): SATResult<PatientSummary[], SATError> {
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    const mapped = safeMapToEntity(
      result.value,
      PatientInfoMapper.toEntityList,
      logContext,
    );
    if (mapped.isFailure) return mapped;
    return SATResult.success(applyPatientFilter(mapped.value, filter));
  }

  async fetchPatient(uuid: string): Promise<SATResult<PatientDetail, SATError>> {
    const result = await this.patientSource.fetchPatient(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      PatientInfoMapper.toDetailEntity,
      "PatientRepositoryImpl::fetchPatient",
    );
  }

  async updatePatient(uuid: string, payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const result = await this.patientSource.updatePatient(uuid, payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async createPatient(payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const result = await this.patientSource.createPatient(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async resendAccountSetupSms(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.patientSource.resendAccountSetupSms(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async activatePatient(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.patientSource.activatePatient(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async deactivatePatient(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.patientSource.deactivatePatient(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }
}

function applyPatientFilter(
  patients: PatientSummary[],
  filter?: PatientListFilter,
): PatientSummary[] {
  if (!filter) return patients;

  return patients.filter((patient) => {
    if (filter.activeOnly && patient.isDeactivated) return false;

    const status = (patient.progressStatus ?? "").toLowerCase();
    if (
      filter.inProgress &&
      !["inprogress", "in_progress", "progress", "active", "진행중", "진행 중"].includes(status)
    ) {
      return false;
    }

    if (
      filter.scheduled &&
      !["scheduled", "notstarted", "not_started", "pending", "시작전", "시작 전"].includes(status)
    ) {
      return false;
    }

    if (filter.lowCompliance) {
      const threshold = filter.lowComplianceThreshold ?? LOW_COMPLIANCE_THRESHOLD;
      const rate = patient.latestComplianceRate;
      if (rate == null || rate >= threshold) return false;
    }

    return true;
  });
}
